/**
 * ThemeToggle — Android-style switch for Light/Dark mode.
 *
 * Lives in the global Header (desktop + mobile). Pure presentation toggle —
 * no analysis logic touched. The hidden checkbox keeps the control
 * keyboard-accessible and announces state to screen readers.
 */
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

export default function ThemeToggle({ size = 'md', className = '' }) {
  const { isDark, toggleTheme } = useTheme();

  const sizes = size === 'sm'
    ? { track: 'h-6 w-11', knob: 'h-4 w-4', knobOn: 'translate-x-5', icon: 'w-3 h-3' }
    : { track: 'h-7 w-12', knob: 'h-5 w-5', knobOn: 'translate-x-5', icon: 'w-3.5 h-3.5' };

  return (
    <label
      className={`inline-flex items-center gap-2 cursor-pointer select-none ${className}`}
      data-testid="theme-toggle"
      title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
    >
      <span
        className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-700'}`}
        data-testid="theme-label-light"
      >
        Light Mode
      </span>
      <span className={`relative inline-flex items-center ${sizes.track} rounded-full transition-colors duration-200 ${isDark ? 'bg-cyan-600' : 'bg-slate-300'}`}>
        <input
          type="checkbox"
          checked={isDark}
          onChange={toggleTheme}
          className="sr-only"
          aria-label={isDark ? 'Disable dark mode' : 'Enable dark mode'}
          data-testid="theme-toggle-checkbox"
        />
        <span
          className={`absolute left-1 inline-flex items-center justify-center ${sizes.knob} rounded-full bg-white shadow-sm transform transition-transform duration-200 ${isDark ? sizes.knobOn : 'translate-x-0'}`}
        >
          {isDark
            ? <Moon className={`${sizes.icon} text-cyan-600`} />
            : <Sun className={`${sizes.icon} text-amber-500`} />}
        </span>
      </span>
      <span
        className={`text-xs font-medium ${isDark ? 'text-slate-100' : 'text-slate-400'}`}
        data-testid="theme-label-dark"
      >
        Dark Mode
      </span>
    </label>
  );
}
